'use client';
import type { NextPage } from 'next';
import { useState } from 'react';
import styles from './time-picker.module.scss';

interface TimePickerProps {
  className?: string;
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
}

const TimePicker: NextPage<TimePickerProps> = ({
  className = '',
  label,
  value = '',
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Generate time options in 30-minute intervals
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        const timeValue = `${hourStr}:${minuteStr}`;
        const timeLabel = formatTime12Hour(hour, minute);
        times.push({ value: timeValue, label: timeLabel });
      }
    }
    return times;
  };

  const formatTime12Hour = (hour: number, minute: number) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const minuteStr = minute.toString().padStart(2, '0');
    return `${hour12}:${minuteStr} ${period}`;
  };

  const timeOptions = generateTimeOptions();

  const handleSelect = (timeValue: string) => {
    onChange?.(timeValue);
    setIsOpen(false);
  };

  const selectedOption = timeOptions.find((option) => option.value === value);
  const displayText = selectedOption?.label || 'Select the time';

  return (
    <div className={[styles.root, className].join(' ')}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles.selectContainer}>
        <div className={styles.selectField} onClick={() => setIsOpen(!isOpen)}>
          <svg
            className={`${styles.clockIcon} ${isOpen ? styles.clockIconOpen : ''}`}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <circle
              cx="8"
              cy="8"
              r="6"
              stroke="currentColor"
              strokeWidth="1.33333"
            />
            <path
              d="M8 4V8L10.5 10.5"
              stroke="currentColor"
              strokeWidth="1.33333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span
            className={`${styles.selectText} ${!selectedOption ? styles.placeholder : ''}`}
          >
            {displayText}
          </span>

          <svg
            className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        {isOpen && (
          <div className={styles.dropdown}>
            {timeOptions.map((option) => (
              <div
                key={option.value}
                className={`${styles.option} ${option.value === value ? styles.selected : ''}`}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimePicker;
